import { SOURCES } from './sources';

export const BAKED_DISHES = [
  {
    slug: "broed-og-dej",
    shortTitle: "Brød, dej og gær",
    title: "Kan man fryse brød, dej og gær?",
    metaTitle: "Kan man fryse brød, dej og frisk gær? — Guide",
    metaDescription: "Ja, brød og boller fryser fremragende i 1–3 mdr. jf. Tænk. Gær kan også fryses ned jf. Samvirke og Arla. Læs råd og fakta her.",
    answer: "Ja, brød og boller fryser særdeles godt i op til 1–3 måneder, og frisk gær kan ligeledes fryses og bruges normalt efter optøning.",
    steps: [
      "Frys brød så friskbagt og nykølet som muligt for at bevare krummens saftighed.",
      "Skær franskbrød og rugbrød i skiver før frysning, så du kan tage præcis det antal skiver op, du skal bruge.",
      "Samvirke bekræfter, at frisk gær udmærket kan fryses og bruges helt som normalt efter optøning.",
      "Arla oplyser, at gærvæske skal være lillefingervarm (37 °C); varmere væske dræber gærcellerne.",
      "Varm frosne boller eller skiver direkte i brødristeren eller i en varm ovn for at genskabe den sprøde skorpe."
    ],
    tableNote: "Kilde: Forbrugerrådet Tænk, Samvirke og Arla",
    table: [
      { food: "Hvedebrød og boller (bagte)", duration: "1–3 måneder", pack: "Lukket frysepose", thaw: "Stuetemperatur eller ovn (5 min)", use: "Lunes i ovn for at genskabe sprød skorpe; 6–10 dage på køkkenbord jf. Tænk" },
      { food: "Rugbrød (i skiver)", duration: "1–3 måneder", pack: "Tætsluttende frysepose", thaw: "Køkkenbord (10–15 min) eller ristes", use: "Bevarer saftighed og kerner; 6–10 dage ved stuetemperatur jf. Tænk" },
      { food: "Frisk bagegær", duration: "Forlænger holdbarhed", pack: "Original indpakning i lille pose", thaw: "Bliver flydende; røres i lun væske (37 °C)", use: "Samvirke bekræfter, at gær kan fryses og bruges normalt" },
      { food: "Ubagt pizzadej / gærdej", duration: "1–2 måneder", pack: "Smurt med lidt olie i frysepose", thaw: "Langsomt i køleskab natten over", use: "Frys før hævning; lad hæve i køleskab" }
    ],
    sections: [
      {
        h2: "Hvorfor bliver brød hurtigere tørt i køleskab?",
        body: `Brød ældes gennem en proces, der kaldes retrogradering: Stivelsesmolekylerne i brødet krystalliserer langsomt og frigiver det bundne vand, hvilket gør brødet tørt og krummende.

Denne proces forløber hurtigst ved køleskabstemperatur. Forbrugerrådet Tænk anbefaler, at både hvedebrød og rugbrød opbevares ved stuetemperatur i en pose med huller, hvor det holder 6–10 dage. Ved frysning under -18 °C standses udtørringen næsten fuldstændigt. Lunes brødet kort i ovnen efter frysning, bliver krummen blød og saftig igen.`
      },
      {
        h2: "Samvirkes og Arlas råd om gær",
        body: `Har du en halv pakke gær tilovers efter bagning, behøver du ikke smide den ud. Samvirke slår fast i sine madspildsråd, at gær kan fryses og bruges helt som normalt, når det er tøet op igen.

Gæren vil ofte blive tynd og flydende under optøning, fordi iskrystaller sprænger nogle af cellevæggene. Arla påpeger i sit råvareleksikon, at væsken som gær røres ud i skal være lillefingervarm (37 °C) – bliver den varmere, dør gærcellerne.`
      },
      {
        h2: "Frysning af ubagt gærdej og pizzadej",
        body: `Du kan også fryse ubagt pizzadej. Nøglen er at fryse dejen umiddelbart efter æltning, inden den når at hæve op.

Form dejen til faste kugler og frys dem i fryseposer. Tag en kugle ud aftenen før og lad den tø langsomt op i en skål i køleskabet. Så koldhæver dejen natten over og er klar til udrulning.`
      }
    ],
    faq: [
      { q: "Kan man fryse frisk gær?", a: "Ja, Samvirke bekræfter, at gær kan fryses og bruges som normalt efter optøning." },
      { q: "Hvorfor må brød ikke ligge i køleskabet?", a: "Stivelsen krystalliserer hurtigst ved køleskabstemperatur, så brødet bliver tørt. Tænk anbefaler stuetemperatur i pose med huller." },
      { q: "Hvor længe holder brød og bagværk i fryseren?", a: "Brød og bagværk kan holde sig 1–3 måneder i fryseren ifølge Forbrugerrådet Tænk." },
      { q: "Hvilken temperatur skal gærvæske have ifølge Arla?", a: "Arla anfører, at væsken skal være lillefingervarm (37 °C); varmere væske dræber gærcellerne." }
    ],
    internalLinks: [
      { text: "Regler for genfrysning af mad", href: "/kan-man-genfryse" },
      { text: "Sådan fryser du mad rigtigt", href: "/saadan-fryser-du-mad" },
      { text: "Holdbarhed i fryseren for brød og kager", href: "/hvor-laenge-holder-mad-i-fryseren" },
      { text: "Frysning af kager og bagværk", href: "/kager" },
      { text: "Frysning af færdigretter", href: "/faerdigretter" }
    ],
    sources: [
      SOURCES.taenkHoldbarhed,
      SOURCES.samvirkeRester,
      SOURCES.arlaGaer
    ]
  },
  {
    slug: "kager",
    shortTitle: "Kager og bagværk",
    title: "Kan man fryse kager og bagværk?",
    metaTitle: "Kan man fryse kager? Lagkage, cheesecake og drømmekage",
    metaDescription: "Faste kager, drømmekage, pandekager og vafler fryser fantastisk i 1–3 mdr. jf. Tænk. Flødeskumskager skiller. Læs hvad der kan fryses her.",
    answer: "Ja, bagte kager som drømmekage, sandkager, vafler og pandekager fryser formidabelt i 1–3 måneder, men flødeskumskager og rå kagecremer tåler ikke frost.",
    steps: [
      "Bag kagen færdig og lad den køle fuldstændig af til stuetemperatur inden frysning.",
      "Skær skærekager i portionsstykker, så du kan tage enkelte kager op ad gangen.",
      "Kager med kokos- eller sukkertopping (som drømmekage) fryses bedst i en fast plastboks frem for en blød pose.",
      "Bagte pandekager og vafler fryses med et stykke mellemlægningspapir imellem for nem adskillelse.",
      "Tø kager langsomt op i køleskabet eller ved stuetemperatur, og lun dem eventuelt kort i ovnen."
    ],
    tableNote: "Kilde: Forbrugerrådet Tænk og Fødevarestyrelsen",
    table: [
      { food: "Drømmekage, sandkage og chokoladekage", duration: "1–3 måneder", pack: "Tætsluttende boks eller pose", thaw: "Stuetemperatur (1 time)", use: "Færdigt bagværk holder 1–3 mdr. jf. Tænk; bevarer saftighed flot" },
      { food: "Brunsviger", duration: "1–3 måneder", pack: "Fast kageboks", thaw: "Lunes i ovn ved 180 °C (5 min)", use: "Sukkerremoncen bliver lækker og blød ved luning" },
      { food: "Pandekager og vafler", duration: "1–3 måneder", pack: "Med madpapir mellem hver i pose", thaw: "Direkte på brødrister eller pande", use: "Genskraber sprødhed og smag lynhurtigt" },
      { food: "Cheesecake (ubagt med husblas/fløde)", duration: "Uegnet til frost", pack: "Husblasstrukturen brydes", thaw: "Bliver vandet og slatten", use: "Bør nydes frisk fra køleskab" },
      { food: "Lagkage med frisk flødeskum og kagecreme", duration: "Uegnet til frysning", pack: "Fløden og cremen skiller", thaw: "Bliver en våd, sammenfalden masse", use: "Kagebunde kan fryses løse; læg først sammen frisk" },
      { food: "Wienerbrød og kanelsnegle", duration: "1–3 måneder", pack: "Frysepose uden glasur", thaw: "Lunes i ovn, pyntes med glasur efter", use: "Glasur smelter og bliver klistret på frost" }
    ],
    sections: [
      {
        h2: "Hvilke kager klarer frost, og hvilke gør ikke?",
        body: `En gylden tommelfingerregel i bagekøkkenet er kagens fedt- og fugtindhold: Kager med en bagt krumme (sandkage, gulerodskage, bananbrød, chokoladekage, brownies) fryser fantastisk. Sukkeret og fedtet i dejen forhindrer store iskrystaller, så krummen forbliver blød og saftig ved optøning.

Kager med rå flødeskum, koldrørte kagecremer, buddinger og husblasfromager (moussekager og ubagte cheesecakes) egner sig derimod ikke til fryseren. Kulden ødelægger husblasens gelerende netværk, så kagen udskiller vand ved optøning, og fløden skiller.`
      },
      {
        h2: "Drømmekage og brunsviger: Lun altid efter frost",
        body: `Klassiske bradepandekager som drømmekage og brunsviger kan udmærket fryses ned. Ved brunsviger vil farin-remoncen have tendens til at krystallisere en smule på frost og miste sin karamelliserede blødhed.

Giv de optøede stykker 4–5 minutter i en forvarmet ovn ved 180 °C. Varmen smelter remoncen igen, så den trækker ned i den lune gærbrødsbund og smager som nybagt.`
      },
      {
        h2: "Pandekager og vafler som lynmåltid",
        body: `Har du bagt en stor stak pandekager eller vafler søndag eftermiddag, så frys overskuddet ned. Læg et lille kvadrat bagepapir mellem hver pandekage, så de ikke fryser sammen.

Når lysten melder sig på en hverdagseftermiddag, vipper du en frossen pandekage direkte på en tør, varm stegepande eller i brødristeren. På 60 sekunder er den gennemvarm og sprød i kanten.`
      }
    ],
    faq: [
      { q: "Kan man fryse lagkage?", a: "Nej, færdigsamlet lagkage med flødeskum og kagecreme skiller og bliver sjasket ved optøning. Bagte lagkagebunde kan derimod fryses løse." },
      { q: "Skal kager med glasur fryses?", a: "Frys helst kagen uden glasur. Flormelisglasur bliver våd og klistret på frost. Smør først frisk glasur på efter optøning." },
      { q: "Hvor længe kan kager og bagværk holde sig i fryseren?", a: "Bagværk holder sig typisk i 1–3 måneder i fryseren ifølge Forbrugerrådet Tænk." },
      { q: "Kan man genfryse optøet kage?", a: "Ja, tørre skærekager uden mejerifyld kan genfryses, hvis de er optøet ved stuetemperatur og pakkes tæt, men de kan miste lidt fugtighed." }
    ],
    internalLinks: [
      { text: "Må man genfryse mad og kager?", href: "/kan-man-genfryse" },
      { text: "Frysning af brød og dej", href: "/broed-og-dej" },
      { text: "Sådan fryser du mad rigtigt", href: "/saadan-fryser-du-mad" },
      { text: "Holdbarhedstabeller for fryseren", href: "/hvor-laenge-holder-mad-i-fryseren" },
      { text: "Frysning af bær til kagefyld", href: "/baer" }
    ],
    sources: [
      SOURCES.taenkHoldbarhed,
      SOURCES.foedevarestyrelsenOpbevaring
    ]
  },
  {
    slug: "faerdigretter",
    shortTitle: "Færdigretter, pasta og ris",
    title: "Kan man fryse færdigretter, pasta og ris?",
    metaTitle: "Kan man fryse lasagne, suppe og kogt pasta/ris? — Guide",
    metaDescription: "Lasagne, dahl og supper fryser fantastisk i 1–3 mdr. jf. Tænk. Kogt pasta varmes til over 75 °C jf. Samvirke. Læs hygiejneråd her.",
    answer: "Ja, lasagne, dahl, supper og gryderetter fryser fremragende i 1–3 måneder; kogt pasta og ris kan også fryses, men kræver hurtig nedkøling og grundig genopvarmning.",
    steps: [
      "Køl varme retter lynhurtigt ned i køleskabet før frysning; stil aldrig en rygende varm gryde i fryseren.",
      "Samvirke anbefaler frysning af kogt pasta; ved genopvarmning skal den nå en temperatur på over 75 grader.",
      "Kogt kød i køleskab holder 5–6 dage ifølge Tænk, hvis det er varmet grundigt igennem og kølet hurtigt ned.",
      "Frys supper og gryderetter i flade fryseposer eller plastbøtter i portionsstørrelser.",
      "Genopvarm altid optøede retter grundigt, så maden er rygende varm og bobler."
    ],
    tableNote: "Kilde: Forbrugerrådet Tænk, Samvirke og Fødevarestyrelsen",
    table: [
      { food: "Færdige retter (lasagne, gryderetter, dahl)", duration: "1–3 måneder", pack: "Ildfast form med låg / frysepose", thaw: "Køleskab eller gryde", use: "Færdige retter holder 1–3 mdr. på frost jf. Tænk" },
      { food: "Kogt pasta (kølet hurtigt ned)", duration: "1–3 måneder", pack: "Tæt frysepose jf. Samvirke", thaw: "Varmes i sauce eller kogende vand", use: "Samvirke: Varmes grundigt igennem til over 75 °C" },
      { food: "Kogte ris (kølet hurtigt ned)", duration: "1–2 måneder", pack: "Portionspose", thaw: "Direkte i varm wok eller mikroovn", use: "Skal køles hurtigt ned pga. bakteriesporer" },
      { food: "Grøntsagssupper (uden fløde)", duration: "1–3 måneder", pack: "Tætsluttende boks / glas (3/4 fuldt)", thaw: "Direkte i gryde", use: "Færdigretter holder 1–3 mdr. jf. Tænk; tilsæt fløde efter opkog" },
      { food: "Havregrød til knækbrød", duration: "Forlænger holdbarhed", pack: "Bøtte i fryseren", thaw: "Blandes med olie, frø og kerner", use: "Samvirke anbefaler frosne havregrødsrester til hjemmebagt knækbrød" }
    ],
    sections: [
      {
        h2: "Samvirkes råd om frossen pasta og havregrød",
        body: `Samvirke fremhæver kogt pasta som en oplagt rest at fryse ned: Har du kogt for meget pasta, så frys resten til senere brug. Det er vigtigt, at den kogte pasta køles hurtigt ned efter tilberedning og ikke står og køler langsomt ved stuetemperatur.

Når pastaen skal spises igen, anbefaler Samvirke, at den varmes grundigt igennem og når en temperatur på over 75 grader. Du kan godt varme pastaen op i en sauce, så længe hele retten bliver ordentligt varm.

Samvirke foreslår desuden at gemme rester af havregrød i en bøtte i fryseren; når bøtten er fyldt, røres grøden med olie, frø og kerner og bages til sprødt knækbrød.`
      },
      {
        h2: "Holdbarhed for færdigretter",
        body: `Ifølge Forbrugerrådet Tænks holdbarhedstabel holder færdige retter i 1–3 måneder i fryseren.

I køleskabet kan tilberedt kød, der er stegt eller kogt, holde sig cirka 5–6 dage ifølge Tænk, forudsat at det er varmet grundigt igennem og kølet hurtigt ned.`
      },
      {
        h2: "Fødevaresikkerhed og nedkøling",
        body: `Fødevarestyrelsen råder altid til at køle varm mad hurtigt ned i køleskabet, før det fryses, og holde råvarer adskilt fra spiseklar mad.

Ved genopvarmning skal madretter varmes ordentligt igennem, så eventuelle mikroorganismer dræbes.`
      }
    ],
    faq: [
      { q: "Hvilken temperatur skal frossen pasta genopvarmes til ifølge Samvirke?", a: "Samvirke anfører, at frossen kogt pasta bør varmes grundigt igennem og nå en temperatur på over 75 grader." },
      { q: "Hvor længe kan frosne færdigretter holde sig?", a: "Færdige retter holder sig i 1–3 måneder i fryseren ifølge Forbrugerrådet Tænk." },
      { q: "Hvor længe holder tilberedt kød i køleskabet?", a: "Stegt eller kogt kød kan holde sig ca. 5–6 dage i køleskabet ifølge Forbrugerrådet Tænk, hvis det er kølet hurtigt ned." },
      { q: "Hvad kan frosne havregrødsrester bruges til?", a: "Samvirke anbefaler at samle frosne havregrødsrester og blande dem med olie, frø og kerner til hjemmebagt knækbrød." }
    ],
    internalLinks: [
      { text: "Regler for genopvarmning og optøning", href: "/optoening" },
      { text: "Må man genfryse færdiglavede retter?", href: "/kan-man-genfryse" },
      { text: "Holdbarhedstabel for mad i fryseren", href: "/hvor-laenge-holder-mad-i-fryseren" },
      { text: "Sådan fryser du mad rigtigt", href: "/saadan-fryser-du-mad" },
      { text: "Frysning i glas og ildfaste fade", href: "/fryse-i-glas" }
    ],
    sources: [
      SOURCES.taenkHoldbarhed,
      SOURCES.samvirkeRester,
      SOURCES.foedevarestyrelsenHygiejne
    ]
  },
  {
    slug: "saucer-og-salater",
    shortTitle: "Saucer og pålægssalater",
    title: "Kan man fryse saucer og pålægssalater?",
    metaTitle: "Kan man fryse bearnaise, hummus og marmelade? — Guide",
    metaDescription: "Brun sovs, kødsovs, hummus og marmelade fryser fantastisk. Mayonnaisebaserede salater og bearnaise skiller fuldstændigt. Læs rådene her.",
    answer: "Ja til opbagte saucer, hummus, marmelade og kødsovs, men nej til bearnaisesauce og mayonnaisebaserede pålægssalater, som skiller fuldstændigt.",
    steps: [
      "Frys opbagte saucer (brun sovs, bechamel) i små bøtter eller fryseposer; pisk dem igennem ved genopvarmning.",
      "Frys aldrig bearnaisesauce, hollandaise eller mayonnaise; emulsionen brydes uopretteligt af frosten.",
      "Hummus fryser fremragende; glat overfladen ud og hæld en tynd hinde olivenolie over før frys.",
      "Marmelade og syltetøj kan fryses i måneder og bevarer bærsmagen langt friskere end henkogning.",
      "Undgå at fryse hønsesalat, italiensk salat og tunmousse lavet med creme fraiche/mayonnaise."
    ],
    tableNote: "Kilde: Forbrugerrådet Tænk og Fødevarestyrelsen",
    table: [
      { food: "Brun sovs og opbagte saucer", duration: "1–3 måneder", pack: "Tæt boks eller isterningbakke", thaw: "Gryde ved svag varme under piskning", use: "Færdigretter/saucer holder 1–3 mdr. jf. Tænk; piskes glat" },
      { food: "Hummus (kikærtedip)", duration: "Forlænger holdbarhed", pack: "Boks med olivenolie på toppen", thaw: "Køleskab langsomt, røres igennem", use: "Bevarer smag og fylde utroligt flot" },
      { food: "Marmelade og bærsyltetøj", duration: "10–12 måneder", pack: "Sylteglas (3/4 fyldt) / plastboks", thaw: "Køleskab", use: "Frugtprodukter holder 10–12 mdr. jf. Tænk" },
      { food: "Bearnaisesauce og hollandaise", duration: "Uegnet til frysning", pack: "Emulsionen brydes", thaw: "Skiller til klar olie og fnug", use: "Kan ikke piskes sammen igen; skal laves frisk" },
      { food: "Pålægssalater (hønsesalat, tunsalat)", duration: "Uegnet til frysning", pack: "Mayonnaisen og creme fraichen skiller", thaw: "Vandet driver fra fyldet", use: "Konsistensen ødelægges; nydes frisk" }
    ],
    sections: [
      {
        h2: "Hvorfor kan bearnaise og mayonnaise ikke fryses?",
        body: `Både bearnaisesauce, hollandaisesauce og mayonnaise er emulsioner, hvor fedtdråber holdes svævende i en vandig væske.

Når disse saucer fryses ned, danner vandet skarpe iskrystaller, der gennembryder hinden omkring fedtdråberne. Når saucen tør op, er emulsionen brudt: Fedtstoffet flyder ovenpå som ren olie, mens bunden består af tynd væske og klumper. Saucen kan ikke piskes sammen igen.`
      },
      {
        h2: "Marmelade og frugtprodukter",
        body: `Ifølge Forbrugerrådet Tænk kan frugt og frugtprodukter holde sig i 10–12 måneder i fryseren.

Frossen marmelade er en fantastisk måde at gemme sommerbær på, fordi bærrenes friske smag og farve bevares langt bedre end ved gentagen henkogning.`
      },
      {
        h2: "Opbagte saucer",
        body: `Klassiske sovse jævnet med mel eller maizena kan fryses ned som færdigretter (1–3 måneder ifølge Tænk).

Varm sovsen op i en kasserolle ved svag varme, mens du pisker energisk med et piskeris. Tilsæt en skvæt kold mælk eller en kold smørklat; det samler sovsen og gør den blank igen.`
      }
    ],
    faq: [
      { q: "Kan man fryse færdigkøbt bearnaisesauce?", a: "Nej, emulsionen brydes fuldstændigt af frosten, og saucen skiller til ren olie og vand ved opvarmning." },
      { q: "Hvor længe kan frugtsyltetøj og marmelade holde sig i fryseren?", a: "Frugt og frugtprodukter holder sig i 10–12 måneder på frost ifølge Forbrugerrådet Tænk." },
      { q: "Hvorfor skiller hønsesalat og pålægssalater i fryseren?", a: "Både mayonnaise og creme fraiche i dressingen ødelægges af frost og udskiller store mængder vand." },
      { q: "Hvor længe holder frosne sovser?", a: "Opbagte saucer holder 1–3 måneder i fryseren ifølge Forbrugerrådet Tænk." }
    ],
    internalLinks: [
      { text: "Fødevarer der ikke tåler frysning", href: "/hvad-kan-man-ikke-fryse" },
      { text: "Sådan fryser du mad rigtigt", href: "/saadan-fryser-du-mad" },
      { text: "Frysning af mejeriprodukter og fløde", href: "/floede" },
      { text: "Frysning af krydderurter og pesto", href: "/krydderurter" },
      { text: "Holdbarhed for retter på frost", href: "/hvor-laenge-holder-mad-i-fryseren" }
    ],
    sources: [
      SOURCES.taenkHoldbarhed,
      SOURCES.foedevarestyrelsenOpbevaring
    ]
  },
  {
    slug: "drikkevarer",
    shortTitle: "Drikkevarer",
    title: "Kan man fryse drikkevarer?",
    metaTitle: "Kan man fryse vin, øl, juice og kaffe? — Læs før du fryser",
    metaDescription: "Frys aldrig øl og danskvand i glasflasker, da de eksploderer. Vinrester og kaffe til iskaffe fryser glimrende jf. Samvirke. Læs guiden her.",
    answer: "Ja til juice, kafferester og vin til madlavning, men ALDRIG kulsyreholdige drikke som øl, sodavand og danskvand i lukkede flasker, da de eksploderer.",
    steps: [
      "Frys aldrig øl, sodavand, danskvand eller mousserende vin i lukkede glasflasker eller dåser; trykket sprænger beholderen med voldsom kraft.",
      "Frys rester af rødvin eller hvidvin i en isterningbakke; terningerne er geniale til at smage sovse og simreretter til.",
      "Samvirke anbefaler at fryse kafferester i en isterningbakke som perfekte isterninger til iskaffe.",
      "Samvirke guider også til frysning af kaffe: Frys kun hele bønner og i originalposen placeret i en lufttæt beholder.",
      "Friskpresset juice udvider sig under indfrysning; efterlad altid ekspansionsplads i flasken."
    ],
    tableNote: "Kilde: Samvirke og Forbrugerrådet Tænk",
    table: [
      { food: "Kafferester (i isterningbakke)", duration: "Forlænger holdbarhed", pack: "Isterningbakke jf. Samvirke", thaw: "Direkte i kold mælk", use: "Samvirke anbefaler kaffeisterninger til iskaffe" },
      { food: "Hele kaffebønner", duration: "Forlænger holdbarhed", pack: "Originalpose i lufttæt boks jf. Samvirke", thaw: "Tages ud i portioner", use: "Samvirke: Frys kun hele bønner for at bevare aroma" },
      { food: "Vinrester (rødvin, hvidvin)", duration: "Forlænger holdbarhed", pack: "Isterningbakke, derefter pose", thaw: "Direkte i gryde til simremad", use: "Tænk: Vin har ingen datomærkning; perfekt i sovs" },
      { food: "Friskpresset frugtjuice", duration: "10–12 måneder", pack: "Plastflaske med ekspansionsplads", thaw: "Køleskab, rystes kraftigt", use: "Frugtprodukter holder 10–12 mdr. jf. Tænk" },
      { food: "Øl og sodavand (i glasflaske/dåse)", duration: "LIVSFARLIGT / UEGNET", pack: "Kulsyretrykket sprænger flasken", thaw: "Knust glas og glassplinter i fryseren", use: "Må ALDRIG fryses i lukkede beholdere" }
    ],
    sections: [
      {
        h2: "Hvorfor eksploderer øl og sodavand i fryseren?",
        body: `Når vand fryser til is, udvider det sig med cirka 9 procent. For kulsyreholdige drikkevarer (øl, sodavand, danskvand, champagne) er det livsfarligt.

Kuldioxid (CO2) er opløst i væsken under tryk. Når vandet fryser til is, presses gassen ud af væsken og opbygger et enormt overtryk i beholderen. Kombinationen af ekspanderende is og komprimeret kulsyregas overstiger glasflaskens styrke, så flasken eksploderer i tusindvis af glasskår i fryseren.`
      },
      {
        h2: "Samvirkes råd om kaffe på frost",
        body: `Samvirke fremhæver kaffe i to henseender:
1. Kolde kafferester: Hæld kafferester i en isterningebakke i stedet for i vasken. Så har du de perfekte isterninger til iskaffe, som ikke vander mælken ud.
2. Kaffebønner: Vil man fryse kaffe for at forlænge holdbarheden, råder kaffeeksperter hos Samvirke til kun at fryse hele bønner, da malet kaffe har større overflade og hurtigt mister aroma. Kaffen skal blive i originalposen og placeres i en lufttæt beholder.`
      },
      {
        h2: "Vinrester til madlavning",
        body: `Forbrugerrådet Tænk nævner, at vin og drikkevarer med en alkoholprocent på eller over 10 ikke er underlagt datomærkning, da de ikke vurderes at kunne blive for gamle.

Har du en åbnet sjat vin tilovers, kan du fryse den i en isterningbakke. Alkohol fryser ved lavere temperatur end vand, så terningerne forbliver let bløde. Kom 2–3 vinterninger direkte i bolognesen eller simreretten for at tilføre dybde og syre.`
      }
    ],
    faq: [
      { q: "Hvordan fryser man kaffe ifølge Samvirke?", a: "Samvirke anbefaler at fryse hele kaffebønner i deres originalpose i en lufttæt beholder, eller fryse kafferester i en isterningbakke til iskaffe." },
      { q: "Må man lægge en øl eller sodavand i fryseren?", a: "Nej, trykket fra ekspanderende is og frigivet kulsyregas vil sprænge flasken eller dåsen, hvilket medfører livsfare for glassplinter." },
      { q: "Hvor længe kan frugtsaft og juice holde på frost?", a: "Frugt og frugtprodukter kan holde sig i 10–12 måneder på frost ifølge Forbrugerrådet Tænk." },
      { q: "Kan man drikke vin, der har været frosset?", a: "Frosne vinterninger er bedst til varme sovse og simreretter, da vinens smagsnuancer i et vinglas forringes af kulde og iltning." }
    ],
    internalLinks: [
      { text: "Kan man fryse i glas og fade?", href: "/fryse-i-glas" },
      { text: "Hvad kan man ikke fryse?", href: "/hvad-kan-man-ikke-fryse" },
      { text: "Sådan fryser du mad rigtigt", href: "/saadan-fryser-du-mad" },
      { text: "Holdbarhedstabel for mad og drikke", href: "/hvor-laenge-holder-mad-i-fryseren" },
      { text: "Frysning af frugt og citrus", href: "/bananer-avocado-og-citrus" }
    ],
    sources: [
      SOURCES.samvirkeKaffe,
      SOURCES.samvirkeRester,
      SOURCES.taenkHoldbarhed
    ]
  }
];
